import { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Shield,
  Zap,
  Lock,
  Sparkles,
  LineChart,
  Wallet,
} from "lucide-react";
import BrandLogo from "@/components/common/BrandLogo";
import { padCountdown, useLaunchCountdown } from "@/lib/launchCountdown";
import { resolveLaunchGateMessage } from "@/lib/launchGateCopy";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";

const TRUST = [
  { icon: Shield, label: "FIU aligned" },
  { icon: Lock, label: "Secure" },
  { icon: Zap, label: "Real-time" },
];

const HIGHLIGHTS = [
  {
    icon: LineChart,
    title: "Spot trading",
    desc: "Charts, order books & major pairs",
  },
  {
    icon: Wallet,
    title: "OTC desk",
    desc: "Locked quotes for large tickets",
  },
];

function CountdownSegment({ value, label }) {
  return (
    <div className="flex flex-col items-center min-w-[52px] sm:min-w-[58px]">
      <span className="text-[26px] sm:text-[30px] font-extrabold tabular-nums text-cm-navy leading-none">
        {padCountdown(value)}
      </span>
      <span className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-cm-grey">
        {label}
      </span>
    </div>
  );
}

function CountdownDivider() {
  return (
    <span
      className="text-[22px] font-light text-cm-grey/40 pb-5 select-none"
      aria-hidden
    >
      :
    </span>
  );
}

/** Centered launch modal — matches CoinMBX landing aesthetic. */
export default function ComingSoonPopup({ message, launchAt, onDismiss }) {
  const countdown = useLaunchCountdown(launchAt);
  const displayMessage = resolveLaunchGateMessage(message);

  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  return createPortal(
    <div
      className="cm-modal-overlay z-[200] p-0 sm:p-4"
      data-testid="coming-soon-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coming-soon-popup-title"
    >
      <button
        type="button"
        className="fixed inset-0 bg-cm-navy/50 backdrop-blur-[3px] -z-10"
        onClick={onDismiss}
        aria-label="Close coming soon popup"
        data-testid="coming-soon-popup-backdrop"
      />

      <div
        className="cm-modal-panel relative w-full max-w-[520px] lg:max-w-[680px] bg-white rounded-[20px] sm:rounded-[24px] shadow-[0_32px_80px_-24px_rgba(29,45,63,0.45)] overflow-y-auto overscroll-contain cm-fade-up mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-white/90 backdrop-blur border border-[var(--cm-border)] grid place-items-center text-cm-grey hover:text-cm-navy hover:bg-white transition-colors shadow-sm"
          aria-label="Close"
          data-testid="coming-soon-popup-close"
        >
          <X size={16} strokeWidth={2.2} />
        </button>

        <div className="grid lg:grid-cols-[0.95fr_1.05fr] lg:items-stretch">
          <div className="relative cm-landing-hero-bg cm-landing-mesh overflow-hidden border-b lg:border-b-0 lg:border-r border-[var(--cm-border)] lg:flex lg:flex-col">
            <div className="absolute inset-0 cm-landing-grid-bg opacity-40 pointer-events-none" />
            <div
              className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-50 blur-3xl pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(2,44,237,0.22) 0%, transparent 70%)" }}
            />

            <div className="relative px-6 sm:px-7 pt-7 pb-6 lg:py-8 lg:px-8 lg:flex-1 lg:flex lg:flex-col">
              <div className="flex items-center gap-2.5">
                <BrandLogo size="sm" />
                <span className="text-[18px] font-extrabold tracking-tight text-cm-navy">CoinMBX</span>
              </div>

              <div className="mt-5 relative rounded-[16px] border border-[var(--cm-border)] bg-white p-2.5 shadow-[0_20px_50px_-28px_rgba(2,44,237,0.35)] lg:flex-1 lg:flex lg:flex-col">
                <div className="flex items-center justify-between px-2 py-1.5 border-b border-[var(--cm-border)] mb-2">
                  <span className="text-[10px] font-bold tracking-wider text-cm-grey uppercase">
                    Platform preview
                  </span>
                  <span className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live soon
                  </span>
                </div>
                <img
                  src="/assets/safari-hero-1.png"
                  alt="CoinMBX platform preview"
                  className="w-full rounded-[10px] object-cover object-top max-h-[160px] sm:max-h-[200px] lg:max-h-none lg:flex-1 lg:min-h-[180px]"
                  draggable={false}
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {TRUST.map(({ icon: Icon, label }) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 border border-[var(--cm-border)] text-[10px] font-semibold text-cm-navy"
                  >
                    <Icon size={11} className="text-cm-blue shrink-0" strokeWidth={2.2} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="px-5 sm:px-8 py-6 sm:py-8 lg:py-8 text-left flex flex-col min-h-0 lg:min-h-[340px]">
            <div>
              <span className="cm-landing-eyebrow">
                <Sparkles size={13} strokeWidth={2.2} aria-hidden />
                Coming soon
              </span>

              <h2
                id="coming-soon-popup-title"
                className="mt-5 text-[26px] sm:text-[28px] font-extrabold tracking-tight text-cm-navy leading-[1.15]"
              >
                Launching
                <span className="cm-landing-hero-accent"> shortly</span>
              </h2>

              <p className="mt-4 text-[14px] sm:text-[15px] leading-[1.7] text-cm-grey">
                {displayMessage}
              </p>
            </div>

            <div className="mt-5 flex-1 flex flex-col gap-4">
              <div className="rounded-[16px] border border-[var(--cm-border)] bg-cm-surface/60 p-3.5 sm:p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cm-blue mb-3">
                  What&apos;s launching
                </p>
                <ul className="space-y-2.5">
                  {HIGHLIGHTS.map(({ icon: Icon, title, desc }) => (
                    <li
                      key={title}
                      className="flex items-center gap-3 rounded-[12px] bg-white border border-[var(--cm-border)] px-3 py-2.5"
                    >
                      <div className="w-9 h-9 shrink-0 rounded-[10px] bg-gradient-to-br from-cm-blue to-cm-blue-vivid grid place-items-center shadow-[0_8px_20px_-12px_rgba(2,45,238,0.55)]">
                        <Icon size={16} className="text-white" strokeWidth={1.9} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-bold text-cm-navy leading-tight">{title}</p>
                        <p className="text-[11px] text-cm-grey leading-snug mt-0.5">{desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {countdown && (
                <div
                  className="rounded-[16px] border border-[var(--cm-border)] bg-white px-4 py-3.5"
                  data-testid="coming-soon-popup-countdown"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cm-blue mb-2.5">
                    Launch countdown
                  </p>
                  <div className="flex items-center justify-center gap-1 sm:gap-2">
                    <CountdownSegment value={countdown.days} label="Days" />
                    <CountdownDivider />
                    <CountdownSegment value={countdown.hours} label="Hours" />
                    <CountdownDivider />
                    <CountdownSegment value={countdown.minutes} label="Mins" />
                    <CountdownDivider />
                    <CountdownSegment value={countdown.seconds} label="Secs" />
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-[var(--cm-border)]">
              <button
                type="button"
                onClick={onDismiss}
                className="w-full h-[50px] rounded-full bg-cm-blue text-white font-bold text-[14px] shadow-[0_16px_40px_-14px_rgba(2,44,237,0.5)] hover:bg-cm-blue-deep hover:-translate-y-0.5 transition-all duration-200"
                data-testid="coming-soon-popup-continue"
              >
                Continue to site
              </button>

              <p className="mt-3 text-center text-[12px] text-cm-grey">
                Browse the homepage while we prepare for launch.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
